/* eslint-disable no-console */
/* eslint-disable no-alert */
import NextArrowIcon from "@dana/Icons/NextArrowIcon";
import PrevArrowIcon from "@dana/Icons/PrevArrowIcon";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import isTouchDevice from "@insite/client-framework/Common/Utilities/isTouchDevice";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import translate from "@insite/client-framework/Translate";
import SkipNav, { SkipNavStyles } from "@insite/content-library/Components/SkipNav";
import Button, { ButtonIcon, ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import useEmblaCarousel from "embla-carousel-react";
import React, { createRef, ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { css, ThemeProps, withTheme } from "styled-components";

interface OwnProps {
    id: string;
    extendedStyles?: CarouselStyles;
    title: string;
    maxNumberOfColumns: number;
    slides: CarouselSlide[];
}

type Props = OwnProps & ThemeProps<BaseTheme> & HasShellContext;

export interface CarouselSlide {
    id: string;
    renderComponent: (options: CarouselSlideOptions) => ReactNode;
}

export interface CarouselSlideOptions {}

export interface CarouselStyles {
    titleText?: TypographyPresentationProps;
    skipCarouselButton?: SkipNavStyles;
    mainContainer?: GridContainerProps;
    prevArrowGridItem?: GridItemProps;
    prevArrowButton?: ButtonPresentationProps;
    nextArrowGridItem?: GridItemProps;
    nextArrowButton?: ButtonPresentationProps;
    carouselGridItem?: GridItemProps;
    carouselWrapper?: InjectableCss;
    carouselContainer?: InjectableCss;
    carouselSlidesContainer?: InjectableCss;
    carouselSlide?: InjectableCss;
    carouselSlideInner?: InjectableCss;
}

export const carouselStyles: CarouselStyles = {
    titleText: {
        variant: "h2",
        css: css`
            text-align: center;
            margin-bottom: 10px;
        `,
    },
    mainContainer: {
        gap: 0,
        css: css`
            margin: 0 auto;
        `,
    },
    prevArrowGridItem: {
        width: 1,
        align: "middle",
        css: css`
            justify-content: flex-start;
            padding-left: 16px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                ])}
            button[class*="ButtonWrapper"] {
                height: 40px;
                min-height: initial;
                display: flex;
                align-items: center;
            }
        `,
    },
    prevArrowButton: {
        variant: "secondary",
        color: "text.main",
        css: css`
            border: 0;
            padding: 0;
            justify-content: center;
        `,
    },
    nextArrowGridItem: {
        width: 1,
        align: "middle",
        css: css`
            justify-content: flex-end;
            padding-right: 16px;
            order: 1;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                ])}
            button[class*="ButtonWrapper"] {
                height: 40px;
                min-height: initial;
                display: flex;
                align-items: center;
            }
        `,
    },
    nextArrowButton: {
        variant: "secondary",
        color: "text.main",
        css: css`
            border: 0;
            padding: 0;
            justify-content: center;
        `,
    },
    carouselGridItem: {
        width: 10,
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-basis: 90%;
                        max-width: 90%;
                    `,
                    css`
                        flex-basis: 90%;
                        max-width: 90%;
                    `,
                    css`
                        flex-basis: 90%;
                        max-width: 90%;
                    `,
                    css`
                        flex-basis: 90%;
                        max-width: 90%;
                    `,
                    css`
                        flex-basis: 90%;
                        max-width: 90%;
                    `,
                ])}
        `,
    },
    carouselWrapper: {
        css: css`
            width: 100%;
            position: relative;
        `,
    },
    carouselContainer: {
        css: css`
            overflow: hidden;
        `,
    },
    carouselSlidesContainer: {
        css: css`
            display: flex;
        `,
    },
    carouselSlide: {
        css: css`
            padding: 0;
            position: relative;
            flex: 0 0 auto;
        `,
    },
    carouselSlideInner: {
        css: css`
            width: 100%;
            height: 100%;
            position: relative;
            padding: 0 20px;
            display: flex;
            flex-direction: column;
        `,
    },
};

const Carousel = ({ shellContext, theme, id, extendedStyles, title, maxNumberOfColumns, slides }: Props) => {
    const afterCarousel = createRef<HTMLSpanElement>();
    const [styles] = useState(() => mergeToNew(carouselStyles, extendedStyles));

    const [emblaRef, embla] = useEmblaCarousel();
    const [windowResizeTime, setWindowResizeTime] = useState(0);
    const [canScrollPrev, setCanScrollPrev] = useState(false);
    const [canScrollNext, setCanScrollNext] = useState(false);
    const [hasInteracted, setHasInteracted] = useState(false);

    // Create ref for VisuallyHidden
    const visuallyHiddenRef = useRef<HTMLSpanElement>(null);


    const setCanScroll = () => {
        if (shellContext.isInShell) {
            return;
        }
        setCanScrollPrev(!!embla && embla.canScrollPrev());
        setCanScrollNext(!!embla && embla.canScrollNext());
    };

    const getSlidesToScroll = useCallback(() => {
        if (typeof window === "undefined") {
            return maxNumberOfColumns;
        }

        let localSlidesToScroll: number;
        const { innerWidth } = window;
        if(innerWidth <= 1280 && innerWidth >= 1024) {
            localSlidesToScroll = 4;
        }
        if (innerWidth < theme.breakpoints.values[1]) {
            localSlidesToScroll = 1;
        } else if (innerWidth < theme.breakpoints.values[2]) {
            localSlidesToScroll = 2;
        } else if (innerWidth < theme.breakpoints.values[3]) {
            localSlidesToScroll = 3;
        } else {
            localSlidesToScroll = 4;
        }
        return Math.min(localSlidesToScroll, maxNumberOfColumns);
    }, [maxNumberOfColumns]);

    const [slidesToScroll, setSlidesToScroll] = useState(0);

    useEffect(() => {
        const newSlidesToScroll = getSlidesToScroll();
        setSlidesToScroll(newSlidesToScroll);
    }, [maxNumberOfColumns, windowResizeTime]);

    useEffect(() => {
        if (!embla) {
            return;
        }


        const updateSelectedSlide = () => {
            const slides = embla.slideNodes();

            // Update ARIA attributes for accessibility
            setTimeout(() => {
                slides.forEach((slide, index) => {
                    if ( slide.classList.contains('is-selected')) {
                        slide.setAttribute('aria-hidden','false');
                        // Remove tabindex for interactive elements within the visible slide
                        slide.querySelectorAll('a, button').forEach((element) => {
                            element.removeAttribute('tabindex');
                        });
                    } else {
                        slide.setAttribute('aria-hidden','true');
                        // Add tabindex="-1" for interactive elements within the hidden slides
                        slide.querySelectorAll('a, button').forEach((element) => {
                            element.setAttribute('tabindex', '-1');
                        });
                    }
                });


                // Find the selected slide (visible) and gather information
                const selectedSlides = slides.filter((slide, index) => {
                    // Assuming the selected slide has the 'is-selected' class
                    return slide.classList.contains('is-selected');
                });

                // Find the index of the selected slide(s)
                const selectedSlideIndexes = selectedSlides.map((slide) => slides.indexOf(slide));

                // Create an accessible string describing the selected slides
                const slideInfo = selectedSlideIndexes
                    .map((index) => `${index + 1}  ${translate('of')} ${slides.length}`)
                    .join(' | ');  // Join all selected slides if multiple

                // Get the product names from the slides (assuming each slide has a data attribute with the product name)
                const productNames = selectedSlides.map((slide) => {
                    const productLink = slide.querySelector('[data-test-selector="productDescriptionLink"] span');
                    return productLink ? `[${productLink.textContent}]` : null;
                });

                // Create a message with the product names or a simpler message
                let message = '';
                if (productNames.length > 0) {
                    message = `${translate('You are viewing products:')} ${productNames.join(', ')} ${translate('out of')} ${slides.length} ${translate('products.')}`;
                } else {
                    message = `${translate('You are currently viewing slide')} ${slideInfo} ${translate('out of')} ${slides.length}`;
                }

                // Update the VisuallyHidden element
                if (visuallyHiddenRef.current) {
                    visuallyHiddenRef.current.innerText = message;
                
                    if (hasInteracted) {
                        visuallyHiddenRef.current.setAttribute('aria-live', 'polite');
                        visuallyHiddenRef.current.setAttribute('aria-atomic', 'true');
                    } 
                    setHasInteracted(true); 
                }

            }, 10);
            
        };
        updateSelectedSlide();
          
        embla.on("init", updateSelectedSlide);
        embla.on("select", () => {
            if(!hasInteracted) {
                setTimeout(() => {
                    if(visuallyHiddenRef.current) {
                        visuallyHiddenRef.current.setAttribute('aria-live', 'polite');
                        visuallyHiddenRef.current.setAttribute('aria-atomic', 'true');
                    }
                }, 10);
                setHasInteracted(true);
            } 
            updateSelectedSlide();
        });

        const onWindowResize = () => {
            setWindowResizeTime(Date.now());
        };

        window.addEventListener("resize", onWindowResize);
        onWindowResize();

        return () => {
            window.removeEventListener("resize", onWindowResize);
        };
    }, [embla]);

    useEffect(() => {
        if (!embla || !slides || slides.length === 0) {
            return;
        }

        embla.reInit({
            align: "start",
            slidesToScroll,
            watchDrag: isTouchDevice(),
            loop: true,
        });
        setCanScroll();
    }, [embla, slidesToScroll, slides]);

    if (!slides || slides.length === 0) {
        return null;
    }

    return (
        <>
            <SkipNav
                text={translate("Skip Carousel")}
                extendedStyles={styles.skipCarouselButton}
                destination={afterCarousel}
            />
            <Typography {...styles.titleText}>{title}</Typography>
            <GridContainer {...styles.mainContainer} data-test-selector="carousel">
                <GridItem {...styles.prevArrowGridItem}>
                    {slides.length > slidesToScroll && (
                        <Button
                            {...styles.prevArrowButton}
                            onClick={() => embla && embla.scrollPrev()}
                            disabled={!canScrollPrev}
                            data-test-selector="prevBtn"
                            aria-label={translate('Previous Arrow')}
                        >
                            <ButtonIcon src={PrevArrowIcon} />
                        </Button>
                    )}
                </GridItem>
                <GridItem {...styles.nextArrowGridItem}>
                    {slides.length > slidesToScroll && (
                        <Button
                            {...styles.nextArrowButton}
                            onClick={() => embla && embla.scrollNext()}
                            disabled={!canScrollNext}
                            data-test-selector="nextBtn"
                            aria-label={translate('Next Arrow')}
                        >
                            <ButtonIcon src={NextArrowIcon} />
                        </Button>
                    )}
                </GridItem>
                <GridItem {...styles.carouselGridItem}>
                    <StyledWrapper {...styles.carouselWrapper}>
                        <StyledWrapper {...styles.carouselContainer} ref={emblaRef}>
                            <StyledWrapper {...styles.carouselSlidesContainer} data-test-selector="slides">
                                {slides.map(slide => (
                                    <StyledWrapper
                                        {...styles.carouselSlide}
                                        key={slide.id}
                                        style={{ width: `calc(100% / ${slidesToScroll})` }}
                                        aria-hidden="false"
                                    >
                                        <StyledWrapper
                                            {...styles.carouselSlideInner}
                                            data-test-key={slide.id}
                                            id={`slide_${slide.id}`}
                                            data-test-selector="carousel_slide"
                                        >
                                            {slide.renderComponent({})}
                                        </StyledWrapper>
                                    </StyledWrapper>
                                ))}
                            </StyledWrapper>
                        </StyledWrapper>
                        <VisuallyHidden>
                            <span ref={visuallyHiddenRef} id="visuallyHiddenElement"/>
                        </VisuallyHidden>
                    </StyledWrapper>
                </GridItem>
            </GridContainer>
            <span ref={afterCarousel} tabIndex={-1} />
        </>
    );
};

Carousel.defaultProps = {
    maxNumberOfColumns: 5,
};

export default withIsInShell(withTheme(Carousel));